import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { JsonDiffService } from '../../../services/env-sync/json-diff.service';
import { CustomFilter, DiffLine } from '../../../services/env-sync/types';
import { Environment, RecordComparisonItem, RecordStatus, SchemaComparisonItem } from '../types';

const FILTER_ALL_VALUE = 'all' as const;

type StatusFilterValue = RecordStatus | typeof FILTER_ALL_VALUE;
type CustomFilterValue = string | typeof FILTER_ALL_VALUE;

const RECORD_STATUS_CONFIG: Record<RecordStatus, { label: string; css: string }> = {
  [RecordStatus.CHANGED]: { label: 'Changed', css: 'success-chip' },
  [RecordStatus.NOT_FOR_PROD]: { label: 'Not for Production', css: 'danger-chip' },
  [RecordStatus.ONLY_IN_PROD]: { label: 'Not in QA', css: 'danger-chip' },
  [RecordStatus.ONLY_IN_QA]: { label: 'Only in QA', css: 'info-chip' },
  [RecordStatus.UNCHANGED]: { label: 'No Changes', css: 'neutral-chip' },
};

@Component({
    selector: 'app-schema-comparison',
    templateUrl: './schema-comparison.component.html',
    styleUrls: ['./schema-comparison.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SchemaComparisonComponent implements OnInit, OnChanges {
  @Input() schemas: SchemaComparisonItem[] = [];
  protected filteredSchemas: Map<string, RecordComparisonItem[]> = new Map();

  protected searchFilter = '';
  private searchSubject = new Subject<string>();

  protected statusFilter: StatusFilterValue = FILTER_ALL_VALUE;
  protected availableStatuses: RecordStatus[] = [];

  protected customFilters: Map<string, CustomFilterValue> = new Map();
  protected availableCustomFilterValues: Map<string, Set<string>> = new Map();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly jsonDiffService: JsonDiffService,
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(200)).subscribe((search) => {
      this.searchFilter = search;
      this.filterSchemas();
      this.cdr.markForCheck();
    });
    this.refreshAvailableStatuses();
    this.refreshAvailableCustomFilterValues();
    this.filterSchemas();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schemas']) {
      this.refreshAvailableStatuses();
      this.refreshAvailableCustomFilterValues();
      this.filterSchemas();
    }
  }

  onSearchKeyup(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase().trim();
    this.searchSubject.next(value);
  }

  onStatusFilterChange(value: StatusFilterValue): void {
    this.statusFilter = value;
    this.filterSchemas();
    this.cdr.markForCheck();
  }

  onCustomFilterChange(filter: CustomFilter, value: CustomFilterValue): void {
    this.customFilters.set(filter.key, value);
    this.filterSchemas();
    this.cdr.markForCheck();
  }

  clearAllFilters(): void {
    this.searchFilter = '';
    this.statusFilter = FILTER_ALL_VALUE;
    this.customFilters.clear();
    this.searchSubject.next('');
    this.filterSchemas();
    this.cdr.markForCheck();
  }

  private filterSchemas(): void {
    this.filteredSchemas.clear();
    for (const schema of this.schemas) {
      const filtered = schema.records.filter(
        (r) =>
          this.matchesSearch(r) && this.matchesStatus(r) && this.matchesCustomFilters(r, schema),
      );
      this.filteredSchemas.set(schema.schemaName, filtered);
    }
  }

  private matchesSearch(record: RecordComparisonItem): boolean {
    return !this.searchFilter || record.label.toLowerCase().includes(this.searchFilter);
  }

  private matchesStatus(record: RecordComparisonItem): boolean {
    return (
      this.statusFilter === FILTER_ALL_VALUE ||
      this.getRecordStatuses(record).includes(this.statusFilter)
    );
  }

  private matchesCustomFilters(
    record: RecordComparisonItem,
    schema: SchemaComparisonItem,
  ): boolean {
    return schema.customFilters.every((cf) => {
      const value = this.customFilters.get(cf.key);
      if (!value || value === FILTER_ALL_VALUE) {
        return true;
      }
      return this.getRecordCustomFilterValues(record, cf).includes(value);
    });
  }

  private refreshAvailableStatuses(): void {
    const statusSet = new Set<RecordStatus>();
    for (const schema of this.schemas) {
      for (const record of schema.records) {
        this.getRecordStatuses(record).forEach((s) => statusSet.add(s));
      }
    }
    this.availableStatuses = Array.from(statusSet).sort();
  }

  private refreshAvailableCustomFilterValues(): void {
    this.availableCustomFilterValues.clear();
    for (const schema of this.schemas) {
      for (const customFilter of schema.customFilters) {
        const values = this.availableCustomFilterValues.get(customFilter.key) ?? new Set<string>();
        for (const record of schema.records) {
          const recordValues = this.getRecordCustomFilterValues(record, customFilter);
          recordValues.forEach((value: string) => {
            if (value) {
              values.add(value);
            }
          });
        }
        this.availableCustomFilterValues.set(customFilter.key, values);
      }
    }
  }

  getFilteredRecords(schema: SchemaComparisonItem): RecordComparisonItem[] {
    return this.filteredSchemas.get(schema.schemaName) ?? [];
  }

  getFilteredSelectableRecords(schema: SchemaComparisonItem): RecordComparisonItem[] {
    return this.getFilteredRecords(schema).filter((r) => this.isRecordSelectable(r));
  }

  hasActiveCustomFilters(): boolean {
    return Array.from(this.customFilters.values()).some((value) => value !== FILTER_ALL_VALUE);
  }

  getCustomFilterValue(filter: CustomFilter): CustomFilterValue {
    return this.customFilters.get(filter.key) ?? FILTER_ALL_VALUE;
  }

  getAvailableCustomFilterValues(filter: CustomFilter): string[] {
    return Array.from(this.availableCustomFilterValues.get(filter.key) ?? []).sort();
  }

  getAllCustomFilters(): CustomFilter[] {
    const allFilters: CustomFilter[] = [];
    for (const schema of this.schemas) {
      allFilters.push(...schema.customFilters);
    }
    const uniqueFilters = allFilters.filter(
      (filter, index, array) => array.findIndex((f) => f.key === filter.key) === index,
    );
    return uniqueFilters;
  }

  private getRecordCustomFilterValues(
    record: RecordComparisonItem,
    customFilter: CustomFilter,
  ): string[] {
    const data = record.qa?.data ?? record.prod?.data;
    if (!data) {
      return [];
    }

    let value: unknown;

    if (data._metadata && data._metadata[customFilter.column]) {
      value = data._metadata[customFilter.column];
    } else if (data[customFilter.column]) {
      value = data[customFilter.column];
    } else {
      return [];
    }

    if (Array.isArray(value)) {
      return value.filter((v) => v != null).map((v) => String(v));
    }

    return value != null ? [String(value)] : [];
  }

  toggleSchemaSelection(schema: SchemaComparisonItem): void {
    const filteredSelectable = this.getFilteredSelectableRecords(schema);
    if (filteredSelectable.length === 0) {
      return;
    }
    const allFilteredSelected = filteredSelectable.every((r) => r.isSelected);
    filteredSelectable.forEach((r) => (r.isSelected = !allFilteredSelected));
    this.cdr.markForCheck();
  }

  toggleRecordSelection(record: RecordComparisonItem, schema: SchemaComparisonItem): void {
    if (!this.isRecordSelectable(record)) {
      return;
    }
    record.isSelected = !record.isSelected;
    this.cdr.markForCheck();
  }

  isSchemaChecked(schema: SchemaComparisonItem): boolean {
    const filteredSelectable = this.getFilteredSelectableRecords(schema);
    return filteredSelectable.length > 0 && filteredSelectable.every((r) => r.isSelected);
  }

  isSchemaIndeterminate(schema: SchemaComparisonItem): boolean {
    const filteredSelectable = this.getFilteredSelectableRecords(schema);
    return (
      filteredSelectable.some((r) => r.isSelected) && !filteredSelectable.every((r) => r.isSelected)
    );
  }

  getSelectableRecords(schema: SchemaComparisonItem): RecordComparisonItem[] {
    return schema.records.filter((r) => this.isRecordSelectable(r));
  }

  getSelectableRecordCount(): number {
    return this.schemas.reduce((count, schema) => {
      return count + this.getSelectableRecords(schema).length;
    }, 0);
  }

  getSelectedRecordCount(): number {
    return this.schemas.reduce((count, schema) => {
      return count + this.getSelectableRecords(schema).filter((r) => r.isSelected).length;
    }, 0);
  }

  getFilteredSelectedCount(): number {
    return this.schemas.reduce((count, schema) => {
      return count + this.getFilteredRecords(schema).filter((r) => r.isSelected).length;
    }, 0);
  }

  getHiddenSelectionsCount(): number {
    return this.getSelectedRecordCount() - this.getFilteredSelectedCount();
  }

  hasHiddenSelections(): boolean {
    return (!!this.searchFilter || !!this.statusFilter) && this.getHiddenSelectionsCount() > 0;
  }

  toggleSchemaExpansion(schema: SchemaComparisonItem): void {
    schema.isExpanded = !schema.isExpanded;
  }

  toggleRecordExpansion(record: RecordComparisonItem): void {
    record.isExpanded = !record.isExpanded;
  }

  isRecordChanged(record: RecordComparisonItem): boolean {
    return record.diff.length > 0;
  }

  isRecordNotForProd(record: RecordComparisonItem): boolean {
    return record.flags?.notForProduction ?? false;
  }

  isRecordOnlyInProd(record: RecordComparisonItem): boolean {
    return !!record.prod?.data && !record.qa?.data;
  }

  isRecordOnlyInQA(record: RecordComparisonItem): boolean {
    return !!record.qa?.data && !record.prod?.data;
  }

  isRecordUnchanged(record: RecordComparisonItem): boolean {
    return (
      !this.isRecordChanged(record) &&
      !this.isRecordNotForProd(record) &&
      !this.isRecordOnlyInProd(record) &&
      !this.isRecordOnlyInQA(record)
    );
  }

  isRecordSelectable(record: RecordComparisonItem): boolean {
    return (
      !this.isRecordNotForProd(record) &&
      (this.isRecordChanged(record) || this.isRecordOnlyInQA(record))
    );
  }

  getRecordStatuses(record: RecordComparisonItem): RecordStatus[] {
    const statuses: RecordStatus[] = [];
    if (this.isRecordChanged(record)) {
      statuses.push(RecordStatus.CHANGED);
    }
    if (this.isRecordNotForProd(record)) {
      statuses.push(RecordStatus.NOT_FOR_PROD);
    }
    if (this.isRecordOnlyInProd(record)) {
      statuses.push(RecordStatus.ONLY_IN_PROD);
    }
    if (this.isRecordOnlyInQA(record)) {
      statuses.push(RecordStatus.ONLY_IN_QA);
    }
    if (this.isRecordUnchanged(record)) {
      statuses.push(RecordStatus.UNCHANGED);
    }
    return statuses;
  }

  getRecordStatusConfig(status: RecordStatus) {
    return RECORD_STATUS_CONFIG[status];
  }

  getQADiffLines(record: RecordComparisonItem): DiffLine[] {
    return this.jsonDiffService.generateDiffLines(record, Environment.QA);
  }

  getProdDiffLines(record: RecordComparisonItem): DiffLine[] {
    return this.jsonDiffService.generateDiffLines(record, Environment.PROD);
  }
}
